/*
* Copyright © 2019 Insticator, Inc. All Rights Reserved.
*
* CONFIDENTIALITY NOTICE: THIS SOFTWARE CONTAINS CONFIDENTIAL INFORMATION OF
* INSTICATOR, INC. USE, DISCLOSURE OR REPRODUCTION IS PROHIBITED WITHOUT THE PRIOR
* EXPRESS WRITTEN PERMISSION OF INSTICATOR, INC.
*/


import { log } from 'utils/logger';

// ++++++++++++ DEVICE ++++++++++++ //
// get users device type
const setDevice = () => {
	if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|ipad|iris|kindle|Android|Silk|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(navigator.userAgent)
	    || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw(n|u)|c55\/|capi|ccwa|cdm|cell|chtm|cldc|cmd|co(mp|nd)|craw|da(it|ll|ng)|dbte|dcs|devi|dica|dmob|do(c|p)o|ds(12|d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(|_)|g1 u|g560|gene|gf5|gmo|go(\.w|od)|gr(ad|un)|haie|hcit|hd(m|p|t)|hei|hi(pt|ta)|hp( i|ip)|hsc|ht(c(| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i(20|go|ma)|i230|iac( ||\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|[a-w])|libw|lynx|m1w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|mcr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|([1-8]|c))|phil|pire|pl(ay|uc)|pn2|po(ck|rt|se)|prox|psio|ptg|qaa|qc(07|12|21|32|60|[2-7]|i)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h|oo|p)|sdk\/|se(c(|0|1)|47|mc|nd|ri)|sgh|shar|sie(|m)|sk0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h|v|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl|tdg|tel(i|m)|tim|tmo|to(pl|sh)|ts(70|m|m3|m5)|tx9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas|your|zeto|zte/i.test(navigator.userAgent.substr(0,4))) {
	    return 'mobile';
	} else {
		return 'desktop';
	}
};
// ++++++++++++ INTEGRATION TYPE ++++++++++++ //

// ++++++++++++ ENVIRONMENT ++++++++++++ //
const getEmbedEnvironment = () => {
    const href = window.parent.location.href;
    if (href.includes('localhost:')) {
        return 'LOCALHOST';
    } else if (href.includes('.hunchme.com') || href.includes('testthisshit.online/staging')) {
        return 'STAGING';
    } else if (href.includes('instireviews.com')) {
        return 'REVIEW';
    } else if (href.includes('insticator.devenv')) {
        return 'DEV';
    } else {
        return 'PRODUCTION';
    }
};

const getIntegrationType = () => {
    try {
        // isInFriendlyIframe is trying to access the most top window's document. If our embed lives within a safe iframe this will throw an error.
        // This error will be then processed in catch() to determine what kind of safe this embed lives in.
        // If accessing the most top window's document doesn't cause any errors, the embed is either in a friendly iframe or hard coded.
        if (isInFriendlyIframe()) {
            return isDFPFriendlyFrame() ? 'DFP_WITHOUT_SAFEFRAME' : 'HARD_CODED_WITH_FRIENDLYFRAME';
        } else {
            return 'HARD_CODING'
        }
    }
    // The iframe is not at all friendly
    catch (e) {
        return isDFPSafeFrame() ? 'DFP_WITH_SAFEFRAME' : 'HARD_CODED_WITH_SAFEFRAME';
    }
};
const isInFriendlyIframe = () => (window.parent.document !== window.top.document);
const isDFPFriendlyFrame = () => (!!window.parent.name && window.parent.name.includes('google') && window.parent.name.includes('friendly'));
const isDFPSafeFrame = () => (window.parent.location.href.includes("googlesyndication") && window.parent.location.href.includes("safeframe"));

const getUrl = () => {
    switch (getIntegrationType()) {
        case 'DFP_WITH_SAFEFRAME':
            return window.parent.document.referrer === '' ? 'not_provided_by_safe_iFrame' : window.parent.document.referrer;
        case 'HARD_CODED_WITH_SAFEFRAME':
            return window.parent.document.referrer;
        default:
            return window.top.location.href;
    }
};
const getWindow = () => {
    // return integration.includes('DFP') ? window.parent.parent : window.parent;
    // this was creating an error in DFP, testing to see if window.parent works, if not put above code back in place
    return window.parent;
};

const checkIfLocalStorageExists = () => {
    const lsTestValue = 'lsEnabledTest';
    try {
        localStorage.setItem(lsTestValue, lsTestValue);
        localStorage.removeItem(lsTestValue);
        return true;
    } catch(e) {
        console.log(e);
        return false;
    }
};

const getLocation = (href) => {
    let location = document.createElement("a");
    location.href = href;
    // IE doesn't populate all link properties when setting .href with a relative URL,
    // however .href will return an absolute URL which then can be used on itself
    // to populate these additional fields.
    if (location.host == "") {
        location.href = location.href;
    }
    return location;
};

const getHost = (href) => {
    let location = getLocation(href);
    return location.hostname;
};


// ----- CREATE VARIABLES FOR EXPORT ----- //
const environment = getEmbedEnvironment();
const integration = getIntegrationType();
const pageurl = getUrl();
const device = setDevice();
const parentWindow = getWindow();
const isLocalStorageExists = checkIfLocalStorageExists();
const hostname = getHost(pageurl);


/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export { device, environment, pageurl, integration, parentWindow, isLocalStorageExists, hostname };
